// src/features/task/InheritanceNotice.tsx
// The one line that says where a setting came from, and the link to go change
// it there.
//
// The sentence is not decoration. Which of the three it is decides what a
// person is about to do when they follow the link: adjust their own project's
// record, walk up to somebody else's, or edit one record that governs every
// project of that kind. Only the third is stated with a warning icon — colour
// alone never carries that, per the accessibility rules, so the icon and the
// words both say it.

import { Link } from 'react-router-dom';
import { useI18n } from '@react/hooks';
import { Button } from '@react/components';
import {
  isKnownScopeKind,
  originAdminRoute,
  originScopeCase,
  originScopeKind,
  originSourceKey,
} from '@/features/task/inheritance';
import type { Entity } from '@core/types/entity';
import type { ResolvedReference, TaskConfigOrigin } from '@core/types/task';

/** How the sentence opens: the status set speaks in the plural, a field does not. */
export type OriginLineKind = 'statuses' | 'value';

export interface OriginLineProps {
  origin: TaskConfigOrigin | undefined;
  /** The scope the form asked about — an origin on it reads as "set here". */
  askedScope: Entity | undefined;
  kind: OriginLineKind;
  className?: string;
}

function WarningIcon() {
  return (
    <svg
      className="mt-0.5 h-3.5 w-3.5 flex-shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

/**
 * Resolve the words for an origin: which sentence, and what fills its slots.
 * Kept next to the component because every caller wants the same wording.
 */
export function useOriginSentence(
  origin: TaskConfigOrigin | undefined,
  askedScope: Entity | undefined,
  kind: OriginLineKind,
) {
  const { t } = useI18n();

  const scopeCase = originScopeCase(origin, askedScope);
  const sourceKey = originSourceKey(origin?.source);
  const source = t(`task.inheritance.source.${sourceKey}`);
  const rawKind = originScopeKind(origin);
  const scopeType = isKnownScopeKind(rawKind)
    ? t(`task.inheritance.scope.${rawKind}`)
    : rawKind;
  const name = origin?.origin_name || t('task.inheritance.unnamedRecord');

  return {
    scopeCase,
    source,
    name,
    sentence: t(`task.inheritance.${kind}.${scopeCase}`, { source, name, scopeType }),
    linkTo: originAdminRoute(origin),
    linkLabel: t('task.inheritance.openRecord', { source, name }),
  };
}

/**
 * "These come from the policy «Backend», set on this project." — plus the way
 * to open «Backend» itself.
 */
export function OriginLine({ origin, askedScope, kind, className = '' }: OriginLineProps) {
  const { scopeCase, sentence, linkTo, linkLabel } = useOriginSentence(origin, askedScope, kind);

  // A record governing every scope of its kind is the only case where opening
  // it and changing something affects people who never saw this screen. It gets
  // the warning surface — the two milder cases stay a quiet line of prose so
  // that the loud one still means something.
  const isBlanket = scopeCase === 'kind';

  // On the warning surface the link inherits the surface's own colour — primary
  // on amber fails contrast in one theme or the other — and stays underlined so
  // it still reads as a link without relying on colour.
  const link = linkTo ? (
    <>
      {' '}
      <Link
        to={linkTo}
        className={`rounded-sm font-medium underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
          isBlanket ? 'hover:opacity-80' : 'text-primary hover:text-primary/80'
        }`}
      >
        {linkLabel}
      </Link>
    </>
  ) : null;

  if (isBlanket) {
    return (
      <div
        className={`alert-warning flex items-start gap-2 rounded-md border px-3 py-2 text-xs leading-relaxed ${className}`}
      >
        <WarningIcon />
        <p className="min-w-0">
          {sentence}
          {link}
        </p>
      </div>
    );
  }

  return (
    <p className={`text-xs leading-relaxed text-muted-foreground ${className}`}>
      {sentence}
      {link}
    </p>
  );
}

/** The two single-value fields that inherit. Only used to pick a noun. */
export type InheritedField = 'template' | 'pipeline';

export interface InheritedValueNoteProps {
  /** What the scope offers, or null when it offers nothing. */
  inherited: ResolvedReference | null;
  /** Name of the inherited record, or '' when it could not be read. */
  inheritedName: string;
  askedScope: Entity | undefined;
  /** True when this type sets its own value, replacing whatever is inherited. */
  isOverridden: boolean;
  isResolving?: boolean;
  resolveError?: string | null;
  onRetryResolve?: () => void;
  field: InheritedField;
}

/**
 * What a person needs to know under a template or pipeline control, in the
 * order the question arises: what happens if I leave this alone, where does
 * that come from, and what changes if I set or clear it.
 *
 * The last part is the reason this exists. Clearing one of these fields does
 * not empty it — it hands the field back to the scope. That is a different
 * outcome from "nothing anywhere", and the two must never read alike.
 */
export function InheritedValueNote({
  inherited,
  inheritedName,
  askedScope,
  isOverridden,
  isResolving,
  resolveError,
  onRetryResolve,
  field,
}: InheritedValueNoteProps) {
  const { t } = useI18n();

  if (isResolving) {
    return (
      <p className="mt-1.5 text-xs text-muted-foreground" role="status" aria-live="polite">
        {t(`task.inheritance.${field}.checking`)}
      </p>
    );
  }

  if (resolveError) {
    return (
      <div className="mt-1.5 space-y-1">
        <p className="text-xs text-destructive">{t(`task.inheritance.${field}.resolveFailed`)}</p>
        {onRetryResolve && (
          <Button size="sm" variant="ghost" onClick={onRetryResolve}>
            {t('common.retry')}
          </Button>
        )}
      </div>
    );
  }

  const name = inheritedName || t('task.inheritance.unnamedValue');

  if (!inherited) {
    return (
      <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
        {isOverridden
          ? t(`task.inheritance.${field}.setWithNothingAbove`)
          : t(`task.inheritance.${field}.noneAnywhere`)}
      </p>
    );
  }

  if (isOverridden) {
    return (
      <div className="mt-1.5 space-y-1">
        <p className="text-xs leading-relaxed text-muted-foreground">
          {t(`task.inheritance.${field}.replaces`, { name })}
        </p>
        <OriginLine origin={inherited.origin} askedScope={askedScope} kind="value" />
      </div>
    );
  }

  return (
    <div className="mt-1.5 space-y-1">
      <OriginLine origin={inherited.origin} askedScope={askedScope} kind="value" />
    </div>
  );
}

/**
 * What an unset control should say it will do. An inherited value is named
 * here rather than in a note beside the field, so the control itself states
 * the outcome instead of reading as an empty box.
 */
export function inheritedPlaceholder(
  t: (key: string, params?: Record<string, string | number>) => string,
  inherited: ResolvedReference | null,
  inheritedName: string,
): string | undefined {
  if (!inherited) return undefined;
  if (!inheritedName) return t('task.inheritance.inheritedUnnamed');
  return t('task.inheritance.inheritedValue', { name: inheritedName });
}

export default OriginLine;
